import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  getDispatchCarSpecs,
  upsertDispatchCarSpec,
  deleteDispatchCarSpec,
  getCommonCarTypeCatalog,
} from "../services/carSpecService";
import type { UpsertDispatchCarSpecRequest } from "../types/carSpec.types";

export const useDispatchCarSpecList = (companyGuid: string | null) =>
  useQuery({
    queryKey: ["dispatchCarSpecs", companyGuid],
    queryFn: () => getDispatchCarSpecs(companyGuid!),
    enabled: !!companyGuid,
  });

/** 차종·옵션 어휘는 표준 스펙에서 오고 거의 바뀌지 않는다 — 드로어를 여닫을 때마다 다시 받지 않는다. */
export const useCommonCarTypeCatalog = () =>
  useQuery({
    queryKey: ["commonCarTypeCatalog"],
    queryFn: getCommonCarTypeCatalog,
    staleTime: 1000 * 60 * 30,
  });

export const useUpsertDispatchCarSpec = (companyGuid: string | null) => {
  const qc = useQueryClient();
  const authGuid = useAuthStore((s) => s.user?.userGuid);

  return useMutation({
    mutationFn: (req: UpsertDispatchCarSpecRequest) =>
      upsertDispatchCarSpec(authGuid ?? "", req),
    onSuccess: () => {
      toast.success("전용 톤수가 저장되었습니다.");
      qc.invalidateQueries({ queryKey: ["dispatchCarSpecs", companyGuid] });
    },
    // 서버 검증 사유(표준 톤수 중복, '미만' 포함, 미등록 차종 등)를 그대로 보여줘야 원인을 안다.
    onError: (err: Error) => {
      toast.error(`저장 실패: ${err.message}`);
    },
  });
};

export const useDeleteDispatchCarSpec = (companyGuid: string | null) => {
  const qc = useQueryClient();
  const authGuid = useAuthStore((s) => s.user?.userGuid);

  return useMutation({
    mutationFn: (guid: string) =>
      deleteDispatchCarSpec(authGuid ?? "", companyGuid!, guid),
    onSuccess: () => {
      toast.success("전용 톤수가 삭제되었습니다.");
      qc.invalidateQueries({ queryKey: ["dispatchCarSpecs", companyGuid] });
    },
    onError: (err: Error) => {
      toast.error(`삭제 실패: ${err.message}`);
    },
  });
};
